import type { AeaExerciseModel, SelectedBookData, TeacherTestDetails } from "p6m-p6u";
import { resolveArticleUuid } from "./UserExercises";

export interface BookFilters {
    blockedSubjects: Array<string>;
    blockedPublishers: Array<string>;
    blockedBooks: Array<string>;
}

/** Whichever of a book's ids are known; a missing id never blocks. */
export interface BookIds {
    subjectId?: string;
    publisherId?: string;
    bookId?: string;
}

/** The content filters of the creation flows: a book is blocked by its subject, publisher or the book itself. */
export const isBlockedByFilters = ({ subjectId, publisherId, bookId }: BookIds, filters: BookFilters): boolean =>
    (!!subjectId && filters.blockedSubjects.includes(subjectId)) ||
    (!!publisherId && filters.blockedPublishers.includes(publisherId)) ||
    (!!bookId && filters.blockedBooks.includes(bookId));

/**
 * The content filters applied to an already loaded band info. Books whose
 * band info is missing count as allowed: the lists that use this can't always resolve one, and
 * losing a book to a failed lookup is worse than letting it through to the check done on selection.
 */
export const isBookBlockedByFilters = (bandInfo: SelectedBookData | undefined, filters: BookFilters): boolean => {
    if (!bandInfo) {
        return false;
    }

    return isBlockedByFilters(
        {
            subjectId: bandInfo.subject?.ID,
            publisherId: bandInfo.publisherBook?.PublisherID,
            bookId: bandInfo.publisherBook?.BookID,
        },
        filters
    );
};

/** The book an activity draws its vocabulary from, in the shape its band info is fetched with. */
export interface ActivityBook {
    id: string;
    /** Trainings reference their book by UUID, tests by article id. */
    isUuid: boolean;
}

export const getTestBook = (test: TeacherTestDetails): ActivityBook | undefined =>
    test.articleId ? { id: `${test.articleId}`, isUuid: false } : undefined;

export const getTrainingBook = (training: AeaExerciseModel, userId: string): ActivityBook | undefined => {
    // Same ownership check as the selection path: isOwnContentTask alone misses own content here.
    const articleUuid = resolveArticleUuid(training, userId);
    return articleUuid ? { id: articleUuid, isUuid: true } : undefined;
};

/** Deduplicated, because resolving these costs one request per book. */
export const collectActivityBooks = (
    tests: Array<TeacherTestDetails>,
    trainings: Array<AeaExerciseModel>,
    userId: string
): Array<ActivityBook> => {
    const books = [...tests.map(getTestBook), ...trainings.map((training) => getTrainingBook(training, userId))].filter(
        Boolean
    ) as Array<ActivityBook>;
    return Array.from(new Map(books.map((book) => [book.id, book])).values());
};
